/**
 * @module events/listeners/listener
 */

/**
 * @typedef {module:utils/model.Model} Model
 * @typedef {Model} Listener
 */
import array from '../../utils/array.js'
import model from '../../utils/model.js'
import object from '../../utils/object.js'
import resolvable from '../../utils/resolvable.js'
import once from '../options/once.js'
import passive from '../options/passive.js'

const { isArray } = Array
const { isFrozen } = Object

const errors = object(null, {
  task: 'listener.task must be a function',
  trigger: 'Unable to trigger multiple types',
  type: 'listener.type must be a string or a frozen array of strings'
})

const options = {
  bubbles: true
}

const isString = value => typeof value === 'string'

const isType = (type, types) => {
  const { length } = types

  return length &&
    types.every(isString) &&
    (!isArray(type) || isFrozen(type))
}

/**
 * @type {Listener}
 */
const listener = object(model(Symbol('listener')), {
  /**
   * @property {?boolean} capture
   * @property {?boolean} once
   * @property {?boolean} passive
   */

  /**
   * @property [function(event)[]] hooks
   */
  hooks: array(),
  /**
   * The task to trigger when the event fires
   * @property {function(EventTarget,Event)} task
   */
  task: null,
  /**
   * @property {string|string[]} type
   */
  type: null,
  /**
   * Stops the listening of the currentTarget
   * @param {?EventTarget} currentTarget
   */
  forget (
    currentTarget = globalThis
  ) {
    const { type } = this
    const types = [type].flat()

    for (const type of types) {
      currentTarget.removeEventListener(type, this, this)
    }
  },
  /**
   * Handles the event
   * @param {Event} event
   */
  async handleEvent (
    event
  ) {
    const { currentTarget } = event
    const { hooks = [] } = this

    let previous

    for (const hook of hooks) {
      previous = hook(event, previous)
    }

    const result = await this.task?.(currentTarget, event)

    await previous?.(result)
  },
  /**
   * Starts the listener of the currentTarget
   * @param {?EventTarget} currentTarget
   */
  listen (
    currentTarget = globalThis
  ) {
    const { task, type } = this
    const types = [type].flat()

    if (typeof task !== 'function') {
      throw new TypeError(errors.task)
    }

    if (!isType(type, types)) {
      throw new TypeError(errors.type)
    }

    for (const type of types) {
      currentTarget.addEventListener(type, this, this)
    }
  },
  /**
   * Triggers the listener of the target
   * @param {EventTarget} target
   * @param {?Event} event
   * @return {Promise.<UIEvent>}
   */
  async trigger (target, event = null) {
    const { type } = this

    if (isArray(type)) {
      throw new TypeError(errors.trigger)
    }

    const { ownerDocument = target } = target
    const { UIEvent } = globalThis
    const [promise, { resolve: task }] = resolvable()
    const onResolved = object(listener, { type, once, passive, task })

    onResolved.listen(target)
    target.dispatchEvent(event ?? new UIEvent(type, options))

    return promise
  }
})

export default listener
